/* eslint-disable import/no-unused-modules */
import { v4 as uuidv4 } from 'uuid';
import {
  DazzleUser,
  DeveloperTier,
  FeatureFlagAction,
  FeatureFlagId,
  GetAllUsersInternalResponse,
  GetDzlUserInternalResponse,
  Order,
  OrderStatus,
  OrderType,
  PromptType,
  RefreshAccessTokenResponse,
  SetDeveloperTierInternalResponse,
  SortBy,
  SortOrder,
  Suggestion,
  SuggestionStatus,
  TimerTaskAction,
  TimerTaskName,
} from '@dazzle/api-client';

import { getDazzleUserAgent, getDeviceId } from 'Common/util/Net';
import { getApi, handleApiError, regenerateApi } from 'Common/api/DazzleApi';
import logRpc from 'Common/util/logRpc';
import { getAuthToken, setAuthToken } from 'Common/util/Auth';
import { getApp, logSumoEvent, ULogSeverity, ULogTag } from 'Common/api/SumoLogic';

const xDeviceID = getDeviceId();
const xUserAgent = getDazzleUserAgent();

declare const WEBSERVER_BASE_URL: string;

export const wrapMethod =
  <Args extends readonly unknown[], T>(fn: (...args: Args) => Promise<T>, methodName: string) =>
  async (...args: Args): Promise<T | undefined> => {
    if (getAuthToken() === undefined) {
      // Fail silently. We are in the process of logging in.
      return undefined;
    }

    try {
      return await fn(...args);
    } catch (e) {
      handleApiError(e, methodName, false);

      if (e.response?.status === 401) {
        void logSumoEvent({
          app: getApp(),
          severity: ULogSeverity.INFO,
          tag: ULogTag.UserAuth,
          message: `[DazzleInternalService] Token expired, refreshing.`,
        });

        const { access_token } = await refreshAccessToken();
        if (access_token) {
          setAuthToken(access_token);

          regenerateApi();
        }
        try {
          return await fn(...args);
        } catch (e2) {
          handleApiError(e2, methodName, true);
        }
      } else {
        handleApiError(e, methodName, true);
      }

      return undefined;
    }
  };

export const setRefreshToken = async (refresh_token: string) => {
  logRpc('sessionSetRefreshTokenPost');

  await getApi().sessionSetRefreshTokenPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    setRefreshTokenRequest: { refresh_token },
  });
};

export const refreshAccessToken = async (): Promise<RefreshAccessTokenResponse> => {
  logRpc('sessionRefreshAccessTokenPost');

  const { data } = await getApi().sessionRefreshAccessTokenPost({ xUserAgent, xDeviceID, xReqID: uuidv4(), body: {} });

  return data;
};

export const conversationGetInternal = wrapMethod(async (conversation_id: string) => {
  logRpc('conversationGetInternalPost');

  const { data } = await getApi().conversationGetInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getConversationInternalRequest: { conversation_id },
  });

  return data;
}, 'conversationGetInternalPost');

export const conversationSendAsDazzle = wrapMethod(
  async ({
    orderId,
    content,
    conversationId,
    imageUrl,
  }: {
    orderId: string;
    content: string;
    conversationId: string;
    imageUrl?: string;
  }) => {
    logRpc('conversationSendAsDazzlePost');

    const { data } = await getApi().conversationSendAsDazzlePost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      sendDazzleMessageRequest: { order_id: orderId, content, conversation_id: conversationId, media_url: imageUrl },
    });

    return data;
  },
  'conversationSendAsDazzlePost'
);

export const orderList = wrapMethod(
  async ({
    assignedToFilter,
    limit,
    pageToken,
    statusFilters = [],
    typeFilter,
    userIdFilter,
  }: {
    assignedToFilter?: string | null;
    limit?: number | null;
    pageToken?: string | null;
    statusFilters?: OrderStatus[];
    typeFilter?: OrderType;
    userIdFilter?: string;
  }) => {
    logRpc('orderListPost');

    const { data } = await getApi().orderListPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getAllDzlOrdersRequest: {
        assigned_to_filter: assignedToFilter,
        limit,
        page_token: pageToken,
        status_filters: statusFilters,
        type_filter: typeFilter,
        user_id_filter: userIdFilter,
      },
    });

    return data;
  },
  'orderListPost'
);

export const orderGet = wrapMethod(async (order_id: string) => {
  logRpc('orderGetPost');

  const { data } = await getApi().orderGetPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getDzlOrderRequest: { order_id },
  });

  return data;
}, 'orderGetPost');

export const orderAssign = wrapMethod(async (assigned_to: string, order_id: string) => {
  logRpc('orderAssignPost');

  const { data } = await getApi().orderAssignPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    dzlAssignOrderRequest: { assigned_to, order_id },
  });

  return data;
}, 'orderAssignPost');

export const orderPlace = wrapMethod(async (order: Order) => {
  logRpc('orderPlacePost');

  const { data } = await getApi().orderPlacePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    placeDzlOrderRequest: { order },
  });

  return data;
}, 'orderPlacePost');

export const orderPayment = wrapMethod(async (order_id: string, final_price: number) => {
  logRpc('orderPaymentPost');

  const { data } = await getApi().orderPaymentPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    dzlOrderPaymentRequest: { final_price, order_id },
  });

  return data;
}, 'orderPaymentPost');

export const orderComplete = wrapMethod(async (order_id: string) => {
  logRpc('orderCompletePost');

  const { data } = await getApi().orderCompletePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    dzlOrderCompleteRequest: { order_id },
  });

  return data;
}, 'orderCompletePost');

export const orderUpdate = wrapMethod(async (order: Order) => {
  logRpc('orderUpdatePost');

  const { data } = await getApi().orderUpdatePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    dzlUpdateOrderRequest: { order },
  });

  return data;
}, 'orderUpdatePost');

export const orderSendPaymentLink = wrapMethod(async (order_id: string) => {
  logRpc('orderSendPaymentLinkPost');

  const { data } = await getApi().orderSendPaymentLinkPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    sendPaymentLinkRequest: { order_id },
  });

  return data;
}, 'orderSendPaymentLinkPost');

export const orderStripeClear = wrapMethod(async (user_id: string) => {
  logRpc('orderStripeClearPost');

  const { data } = await getApi().orderStripeClearPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    clearDzlStripeRequest: { user_id },
  });

  return data;
}, 'orderStripeClearPost');

export const promptFetch = wrapMethod(async (prompt_types?: Array<PromptType>) => {
  logRpc('promptFetchPost');

  const { data } = await getApi().promptFetchPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    dzlFetchPromptsRequest: { prompt_types },
  });

  return data;
}, 'promptFetchPost');

export const promptUpdate = wrapMethod(async (prompt_type: PromptType, updated_prompt: string) => {
  logRpc('promptUpdatePost');

  const { data } = await getApi().promptUpdatePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    dzlUpdatePromptRequest: { prompt_type, updated_prompt },
  });

  return data;
}, 'promptUpdatePost');

export const userGetInternal = wrapMethod(async (user_id: string): Promise<GetDzlUserInternalResponse | undefined> => {
  logRpc('userGetInternalPost');

  const { data } = await getApi().userGetInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getDzlUserInternalRequest: { user_id },
  });

  return data;
}, 'userGetInternalPost');

export const userSetDeveloperTierInternal = wrapMethod(async (user_lid: number, tier: DeveloperTier | null): Promise<SetDeveloperTierInternalResponse | undefined> => {
  logRpc('userSetDeveloperTierInternalPost');

  const { data } = await getApi().userSetDeveloperTierInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    setDeveloperTierInternalRequest: { user_lid, tier },
  });

  return data;
}, 'userSetDeveloperTierInternalPost');

export const userUpdateInternal = wrapMethod(async (user: DazzleUser) => {
  logRpc('userUpdateInternalPost');

  const { data } = await getApi().userUpdateInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    updateDzlUserInternalRequest: { user },
  });

  return data;
}, 'userUpdateInternalPost');

export const conversationGetConversationsInternal = wrapMethod(
  async ({
    page_token,
    limit,
    user_id,
    user_lid,
  }: {
    page_token?: string;
    limit?: number;
    user_id?: string;
    user_lid?: number;
  }) => {
    logRpc('conversationGetConversationsInternalPost');

    const { data } = await getApi().conversationGetConversationsInternalPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getConversationsInternalRequest: { page_token, limit, user_id, user_lid },
    });

    return data;
  },
  'conversationGetConversationsInternalPost'
);

export const suggestionGetInternal = wrapMethod(async (suggestion_id: string) => {
  logRpc('suggestionGetInternalPost');

  const { data } = await getApi().suggestionGetInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getSuggestionInternalRequest: { suggestion_id },
  });

  return data;
}, 'suggestionGetInternalPost');

export const suggestionGetReviewableInternal = wrapMethod(
  async ({
    page_token,
    limit,
    status_filter,
    user_lid,
  }: {
    page_token?: string;
    limit?: number;
    status_filter?: SuggestionStatus;
    user_lid?: number;
  }) => {
    logRpc('suggestionGetReviewableInternalPost');

    const { data } = await getApi().suggestionGetReviewableInternalPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getReviewableSuggestionsInternalRequest: { page_token, limit, status_filter, user_lid },
    });

    return data;
  },
  'suggestionGetReviewableInternalPost'
);

export const suggestionGetDedupedInternal = wrapMethod(
  async ({ page_token, limit, user_lid }: { page_token?: string; limit?: number; user_lid: number }) => {
    logRpc('suggestionGetDedupedInternalPost');

    const { data } = await getApi().suggestionGetDedupedSuggestionsInternalPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getDedupedSuggestionsInternalRequest: { page_token, limit, user_lid },
    });

    return data;
  },
  'suggestionGetDedupedInternalPost'
);

export const suggestionUpdateInternal = wrapMethod(async (suggestion: Suggestion) => {
  logRpc('suggestionUpdateInternalPost');

  const { data } = await getApi().suggestionUpdateInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    updateDzlSuggestionInternalRequest: { suggestion },
  });

  return data;
}, 'suggestionUpdateInternalPost');

export const suggestionsUpdateStatusInternal = wrapMethod(
  async (suggestion_ids: string[], status: SuggestionStatus) => {
    logRpc('suggestionUpdateStatusInternalPost');

    const { data } = await getApi().suggestionUpdateStatusInternalPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      updateDzlSuggestionsStatusInternalRequest: { suggestion_ids, status },
    });

    return data;
  },
  'suggestionUpdateStatusInternalPost'
);

export const regenerateIdeas = wrapMethod(
  async ({
    userLid,
    mode,
    deleteExisting,
    lookbackDays,
    maxSuggestions,
    includeUserProfile,
  }: {
    userLid: number;
    mode: 'ideas' | 'photo_groups' | 'both';
    deleteExisting?: boolean;
    lookbackDays?: number;
    maxSuggestions?: number;
    includeUserProfile?: boolean;
  }) => {
    logRpc('photoGroupRegenerateIdeasPost');

    const { data } = await getApi().photoGroupRegenerateIdeasPost({
      regenerateIdeasRequest: {
        user_lid: userLid,
        mode,
        delete_existing: deleteExisting ?? false,
        lookback_days: lookbackDays ?? 730,
        max_suggestions: maxSuggestions ?? 8,
        include_user_profile: includeUserProfile ?? true,
      },
    });

    return data;
  },
  'photoGroupRegenerateIdeasPost'
);

export const getUserPhotoGroupsAndIdeas = wrapMethod(
  async ({ userLid, includeS3Urls }: { userLid: number; includeS3Urls?: boolean }) => {
    logRpc('photoGroupGetUserDataPost');

    const { data } = await getApi().photoGroupGetUserDataPost({
      getUserPhotoGroupsAndIdeasRequest: { user_lid: userLid, include_s3_urls: includeS3Urls ?? true },
    });

    return data;
  },
  'getUserPhotoGroupsAndIdeas'
);

export const regenerateStart = async ({
  userLid,
  mode,
  deleteExisting,
}: {
  userLid: number;
  mode: 'ideas' | 'photo_groups' | 'both';
  deleteExisting?: boolean;
}): Promise<{ success: boolean } | undefined> => {
  logRpc('photoGroupRegenerateStartPost');

  const token = getAuthToken();
  if (!token) return undefined;

  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/photo/group/regenerate-start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Device-Id': xDeviceID,
        'X-User-Agent': xUserAgent,
        'X-Request-Id': uuidv4(),
      },
      body: JSON.stringify({ user_lid: userLid, mode, delete_existing: deleteExisting ?? false }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    handleApiError(e, 'photoGroupRegenerateStartPost');
    return undefined;
  }
};

export const regenerateLogs = async ({ userLid }: { userLid: number }): Promise<{ logs: string } | undefined> => {
  logRpc('photoGroupRegenerateLogsPost');

  const token = getAuthToken();
  if (!token) return undefined;

  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/photo/group/regenerate-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Device-Id': xDeviceID,
        'X-User-Agent': xUserAgent,
        'X-Request-Id': uuidv4(),
      },
      body: JSON.stringify({ user_lid: userLid }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    handleApiError(e, 'photoGroupRegenerateLogsPost');
    return undefined;
  }
};

export const generateProfileStart = async ({
  userLid,
  stageId,
}: {
  userLid: number;
  stageId: string;
}): Promise<{ success: boolean; ideas_count: number } | undefined> => {
  logRpc('photoGroupGenerateProfileStartPost');

  const token = getAuthToken();
  if (!token) return undefined;

  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/photo/group/generate-profile-start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Device-Id': xDeviceID,
        'X-User-Agent': xUserAgent,
        'X-Request-Id': uuidv4(),
      },
      body: JSON.stringify({ user_lid: userLid, stage_id: stageId }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    handleApiError(e, 'photoGroupGenerateProfileStartPost');
    return undefined;
  }
};

export const revereFeatureFlagCommand = wrapMethod(async (action: string, flagId: string, value?: string) => {
  logRpc('revereFeatureFlagCommandPost');

  const { data } = await getApi().revereFeatureFlagCommandPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    featureFlagCommandRequest: { action: action as FeatureFlagAction, flag_id: flagId as FeatureFlagId, value },
  });

  return data;
}, 'revereFeatureFlagCommandPost');

export const revereTimerTaskCommand = wrapMethod(async (action: string, taskName: string, frequencyMin?: number) => {
  logRpc('revereTimerTaskCommandPost');

  const { data } = await getApi().revereTimerTaskCommandPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    timerTaskCommandRequest: {
      action: action as TimerTaskAction,
      task_name: taskName as TimerTaskName,
      frequency_min: frequencyMin,
    },
  });

  return data;
}, 'revereTimerTaskCommandPost');

export const revereGetFeatureFlagNames = wrapMethod(async () => {
  logRpc('revereFeatureFlagNamesPost');

  const { data } = await getApi().revereFeatureFlagNamesPost({ xUserAgent, xDeviceID, xReqID: uuidv4() });

  return data;
}, 'revereFeatureFlagNamesPost');

export const revereGetTimerTaskNames = wrapMethod(async () => {
  logRpc('revereTimerTaskNamesPost');

  const { data } = await getApi().revereTimerTaskNamesPost({ xUserAgent, xDeviceID, xReqID: uuidv4() });

  return data;
}, 'revereTimerTaskNamesPost');

export const revereGetFeatureFlagState = wrapMethod(async (flagId: string) => {
  logRpc('revereFeatureFlagStatePost');

  const { data } = await getApi().revereFeatureFlagStatePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getFeatureFlagStateRequest: { flag_id: flagId as FeatureFlagId },
  });

  return data;
}, 'revereFeatureFlagStatePost');

export const revereGetTimerTaskState = wrapMethod(async (taskName: string) => {
  logRpc('revereTimerTaskStatePost');

  const { data } = await getApi().revereTimerTaskStatePost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getTimerTaskStateRequest: { task_name: taskName as TimerTaskName },
  });

  return data;
}, 'revereTimerTaskStatePost');

export const suggestionGetQualityFeedbackStatsInternal = wrapMethod(
  async ({ daysBack }: { daysBack?: number } = {}) => {
    logRpc('suggestionGetQualityFeedbackStatsInternalPost');

    const { data } = await getApi().suggestionGetQualityFeedbackStatsInternalPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getQualityFeedbackStatsInternalRequest: { days_back: daysBack },
    });

    return data;
  },
  'suggestionGetQualityFeedbackStatsInternalPost'
);

export const userGetAllInternal = wrapMethod(
  async ({
    limit,
    pageToken,
    sort_by,
    sort_order,
  }: {
    limit?: number;
    pageToken?: string;
    sort_by?: SortBy;
    sort_order?: SortOrder;
  }): Promise<GetAllUsersInternalResponse | undefined> => {
    logRpc('authGetAllUsersInternalPost');

    const { data } = await getApi().authGetAllUsersInternalPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getAllUsersInternalRequest: { limit: limit ?? 50, page_token: pageToken, sort_by, sort_order },
    });

    return data;
  },
  'authGetAllUsersInternalPost'
);

export const traceListInternal = wrapMethod(
  async ({ user_lid, conversation_id }: { user_lid: number; conversation_id: string }) => {
    logRpc('traceListPost');

    const { data } = await getApi().traceListPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      listTracesRequest: { user_lid, conversation_id },
    });

    return data;
  },
  'traceListPost'
);

export const traceGetInternal = wrapMethod(
  async ({
    user_lid,
    conversation_id,
    message_id,
  }: {
    user_lid: number;
    conversation_id: string;
    message_id: string;
  }) => {
    logRpc('traceGetPost');

    const { data } = await getApi().traceGetPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getTraceRequest: { user_lid, conversation_id, message_id },
    });

    return data;
  },
  'traceGetPost'
);

export const mcpTraceListInternal = wrapMethod(
  async ({
    user_lid,
    page_token,
    limit,
  }: {
    user_lid: number;
    page_token?: string;
    limit?: number;
  }) => {
    logRpc('mcpTraceListPost');

    const { data } = await getApi().mcpTraceListPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      listMcpTracesRequest: { user_lid, page_token, limit: limit ?? 50 },
    });

    return data;
  },
  'mcpTraceListPost'
);

export const mcpTraceGetInternal = wrapMethod(
  async ({ user_lid, trace_id }: { user_lid: number; trace_id: string }) => {
    logRpc('mcpTraceGetPost');

    const { data } = await getApi().mcpTraceGetPost({
      xUserAgent,
      xDeviceID,
      xReqID: uuidv4(),
      getMcpTraceRequest: { user_lid, trace_id },
    });

    return data;
  },
  'mcpTraceGetPost'
);

// --- Onboarding ---

export const onboardingGetEventsInternal = wrapMethod(async ({ user_lid }: { user_lid: number }) => {
  logRpc('onboardingGetEventsInternalPost');

  const { data } = await getApi().onboardingGetEventsInternalPost({
    xUserAgent,
    xDeviceID,
    xReqID: uuidv4(),
    getOnboardingEventsInternalRequest: { user_lid },
  });

  return data;
}, 'onboardingGetEventsInternalPost');

// --- Eval ---

const evalPost = async (path: string, body: Record<string, unknown>) => {
  const token = getAuthToken();
  if (!token) return undefined;

  try {
    const response = await fetch(`${WEBSERVER_BASE_URL}/api/dazzle/eval${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Device-Id': xDeviceID,
        'X-User-Agent': xUserAgent,
        'X-Request-Id': uuidv4(),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (e) {
    handleApiError(e, 'evalPost');
    return undefined;
  }
};

export const evalListDatasetEntries = async (suite: string, limit = 100, offset = 0) => {
  return evalPost('/dataset/list', { suite, limit, offset });
};

export const evalCreateDatasetEntry = async (
  suite: string,
  input_data: Record<string, unknown>,
  expected_data: Record<string, unknown>,
  description?: string,
  contributor?: string
) => {
  return evalPost('/dataset/create', { suite, input_data, expected_data, description, contributor });
};

export const evalUpdateDatasetEntry = async (
  entry_id: string,
  updates: {
    expected_data?: Record<string, unknown>;
    input_data?: Record<string, unknown>;
    description?: string;
    is_active?: boolean;
  }
) => {
  return evalPost('/dataset/update', { entry_id, ...updates });
};

export const evalDeleteDatasetEntry = async (entry_id: string) => {
  return evalPost('/dataset/delete', { entry_id });
};

export const evalStartRun = async (suite: string) => {
  return evalPost('/run/start', { suite });
};

export const evalGetRun = async (run_id: string) => {
  return evalPost('/run/get', { run_id });
};

export const evalListRuns = async (suite: string, limit = 20) => {
  return evalPost('/run/list', { suite, limit });
};

export const evalGetRunResults = async (run_id: string, failures_only = false) => {
  return evalPost('/run/results', { run_id, failures_only });
};

export const evalListSeedCandidates = async (suite: string, user_lid: number, limit = 100, cursor?: number) => {
  return evalPost('/dataset/seed-candidates', { suite, user_lid, limit, cursor });
};
